import axios from 'axios';
import config from '../config';
import { getBearerToken } from '../util/cookie';
import { userDisplayNameAsString } from '../util/data';
import { fetchLivekitCredentials, buildJoinUrl } from './webrtc.duck';
// import { eventsError, eventsRequest, eventsSuccess } from './activities.duck';

// ================ Action types ================ //

export const MY_ACTIVITIES_REQUEST = 'app/ClientSaaS/MY_ACTIVITIES_REQUEST';
export const MY_ACTIVITIES_SUCCESS = 'app/ClientSaaS/MY_ACTIVITIES_SUCCESS';
export const MY_ACTIVITIES_ERROR = 'app/ClientSaaS/MY_ACTIVITIES_ERROR';

export const MY_ACTIVITY_REQUEST = 'app/ClientSaaS/MY_ACTIVITY_REQUEST';
export const MY_ACTIVITY_SUCCESS = 'app/ClientSaaS/MY_ACTIVITY_SUCCESS';
export const MY_ACTIVITY_ERROR = 'app/ClientSaaS/MY_ACTIVITY_ERROR';

// ================ Reducer ================ //

const initialState = {
  myActivities: [],
  myActivitiesError: null,
  myActivitiesFetching: false,

  myActivity: null,
  myActivityError: null,
  myActivityFetching: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case MY_ACTIVITIES_REQUEST:
      return {
        ...state,
        myActivitiesFetching: true,
      };
    case MY_ACTIVITIES_SUCCESS:
      return {
        ...state,
        myActivitiesFetching: false,
        myActivities: payload,
      };
    case MY_ACTIVITIES_ERROR:
      return {
        ...state,
        myActivitiesFetching: false,
        myActivitiesError: payload,
      };

    case MY_ACTIVITY_REQUEST:
      return {
        ...state,
        myActivityFetching: true,
      };
    case MY_ACTIVITY_SUCCESS:
      return {
        ...state,
        myActivityFetching: false,
        myActivity: payload,
      };
    case MY_ACTIVITY_ERROR:
      return {
        ...state,
        myActivityFetching: false,
        myActivityError: payload,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const myActivitiesRequest = () => ({ type: MY_ACTIVITIES_REQUEST });
export const myActivitiesSuccess = data => ({ type: MY_ACTIVITIES_SUCCESS, payload: data });
export const myActivitiesError = error => ({ type: MY_ACTIVITIES_ERROR, payload: error });

export const myActivityRequest = () => ({ type: MY_ACTIVITY_REQUEST });
export const myActivitySuccess = data => ({ type: MY_ACTIVITY_SUCCESS, payload: data });
export const myActivityError = error => ({ type: MY_ACTIVITY_ERROR, payload: error });

// ================ Thunks ================ //

export const getAllMyActivities = () => async (dispatch, getState, sdk) => {
  dispatch(myActivitiesRequest());

  // Returns the fetched activities (or null on failure) so callers that
  // need the data right away — e.g. ClientInvitationAcceptPage surfacing
  // a welcome popup immediately after accepting an invite — can await it,
  // instead of only relying on the redux store updating on a later render.
  return axios
    .get(`${config.coachSaaSService}client/my-activities`, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    })
    .then(res => {
      dispatch(myActivitiesSuccess(res.data));
      return res.data;
    })
    .catch(err => {
      console.log(err);
      dispatch(myActivitiesError(err));
      return null;
    });
};

export const getAClientActivty = activityId => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}client/my-activities/get-a-client-activity`,
    { activityId },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

// Verify a built-in video-call Join deep-link server-side. Returns
// { ok, role, roomName } — role is 'coach' | 'client' and roomName is the
// deterministic LiveKit room for this coach/client pair, on success;
// ok:false otherwise.
export const resolveSessionJoin = ({ activityId, sid, jt }) => async (
  dispatch,
  getState,
  sdk
) => {
  try {
    const res = await axios.post(
      `${config.coachSaaSService}client/my-activities/session-join/resolve`,
      { activityId, sid, jt },
      {
        headers: {
          'content-type': 'application/json',
          Authorization: await getBearerToken(sdk),
        },
      }
    );
    return {
      ok: true,
      role: res.data && res.data.role,
      roomName: res.data && res.data.roomName,
    };
  } catch (e) {
    return { ok: false };
  }
};

// Combines resolveSessionJoin (verify the signed calendar/email link, get the
// LiveKit room) with the same credential-mint + join-URL-build steps the
// existing ad-hoc call flow uses (webrtc.duck.js's offer/answer) and the
// pre-acceptance invitation join flow already uses (clients.duck.js's
// joinInvitationVideoCall), so SessionJoinPage can open the call directly
// instead of routing through the dashboard's Start Session flow (which also
// flips the session to "ongoing" and rings the other party).
export const joinSessionVideoCall = ({ activityId, sid, jt }) => async (
  dispatch,
  getState,
  sdk
) => {
  const resolved = await dispatch(resolveSessionJoin({ activityId, sid, jt }));
  if (!resolved.ok || !resolved.roomName) return { ok: false };

  const { currentUser } = getState().user || {};
  const myName = userDisplayNameAsString(currentUser, '');

  try {
    const { url, token } = await fetchLivekitCredentials(sdk, resolved.roomName, myName);
    return {
      ok: true,
      role: resolved.role,
      joinUrl: buildJoinUrl(resolved.roomName, token, url, myName),
    };
  } catch (e) {
    return { ok: false };
  }
};

export const getMyActivity = activityId => async (dispatch, getState, sdk) => {
  dispatch(myActivityRequest());
  return axios
    .get(`${config.coachSaaSService}client/my-activities/${activityId}`, {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    })
    .then(res => {
      dispatch(myActivitySuccess(res.data));
    })
    .catch(err => {
      dispatch(myActivityError(err));
    });
};

export const getAuthorAvailability = transactionId => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}client/my-activities/getAuthorAvailability`,
    {
      activityid: transactionId,
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const scheduleFromClient = (dates, txId, sessId, videoCall, meetingLocation, idempotencyKey) => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}client/my-activities/schedule`,
    {
      dates: dates,
      transactionId: txId,
      sessionId: sessId,
              ...(videoCall && { videoCall }),
              ...(meetingLocation && { meetingLocation }),
              ...(idempotencyKey && { idempotencyKey }),
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

export const getAuthorAvailableSlots = (transactionId, sessionIndex, date) => async (
  dispatch,
  getState,
  sdk
) => {
  // console.log({transactionId, date});
  // return ;
  return axios.post(
    `${config.coachSaaSService}client/my-activities/getAuthorAvailableSlots`,
    {
      activityid: transactionId,
      sessionindex: sessionIndex,
      date: date,
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

// Same free-slot computation as getAuthorAvailableSlots, but for a coach
// proposing a first-meeting time as part of a client invitation — before any
// activity/session/engagement exists to scope the usual call to. The caller
// is always the coach checking their OWN calendar (no participant to
// intersect with yet), so this only needs a session length, not an activity
// + session index.
export const getInvitationAvailableSlots = (durationMinutes, date) => async (
  dispatch,
  getState,
  sdk
) => {
  return axios.post(
    `${config.coachSaaSService}client/my-activities/getInvitationAvailableSlots`,
    {
      durationMinutes: durationMinutes,
      date: date,
    },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};
